import ModalEnum from '@/ship/Enums/ModalEnum';
import PermissionsEnum from '@/ship/Enums/PermissionsEnum';
import { PMPlanChartEnum, PMTabTypeEnum } from '@/ship/Enums/ProjectManagementEnums';
import { ReportDetail } from '@/ship/Enums/ReportDetailEnum';
import { IPMChart, IPMChartDpcRow } from '@/ship/Models/IProjectManagement';
import { GetRequestParams } from '@/ship/Requests/RequestParams';
import { ProjectManagementAction, ProjectManagementState } from '@/store/ProjectManagement/ProjectManagementRoot';
import { deletePMChartDpc, getPdfPMPlanChart } from '@/utils/api/ProjectManagement/PMChart';
import ErrorService from '@/utils/ErrorService';
import { downloadFile } from '@/utils/FilesDownloading';
import { fromISODateToLocal } from '@/utils/Helper';
import { hasPermission } from '@/utils/Permissions';
import { Vue, Component } from 'vue-property-decorator';

@Component
export default class VReportMixin extends Vue {
    @ProjectManagementAction GET_CHART_BY_ID!: (params: { id: number; params?: GetRequestParams }) => Promise<IPMChart>;
    @ProjectManagementState chart!: IPMChart;

    readonly PMTabTypeEnum = PMTabTypeEnum;
    readonly ReportDetail = ReportDetail;

    get chartId() {
        const id = Number(this.$route.params.id);
        return isNaN(id) ? null : id;
    }

    get isHasPermissionCreate() {
        return hasPermission(PermissionsEnum.PMCreateDetailedSchedule);
    }

    get isHasPermissionDelete() {
        return hasPermission(PermissionsEnum.PMDeleteDetailedSchedule);
    }

    /**
     * @description prepare title for the 'name' col in row
     */
    reportTitle(row: IPMChartDpcRow): string {
        const { startDate, endDate } = row;

        const isValidRange = startDate && endDate;

        if (!isValidRange) {
            return this.$t('page.projectManagement.chart.periodNotChosen') as string;
        }

        return `${fromISODateToLocal(startDate)} - ${fromISODateToLocal(endDate)}`;
    }

    openDetailPage(name: ReportDetail, docId?: number) {
        const params = {
            id: `${this.chartId}`,
            ...(docId ? { docId: `${docId}` } : {}),
        };
        this.$router.push({
            name,
            params,
        });
    }

    setChartRows<T>(reports: T[]) {
        return reports.map((report) => ({ ...report, selected: false }));
    }

    protected async initChart() {
        if (this.chartId) await this.GET_CHART_BY_ID({ id: this.chartId });
    }

    /**
     * Запрос на получение печатной формы и ее открытие
     * @param {number} id - идентификатор
     */
    protected async printReport(id: number, type: PMPlanChartEnum) {
        try {
            const { data } = await getPdfPMPlanChart(id, type as string);

            const anchor = document.createElement('a');
            anchor.href = data.toString();
            anchor.target = '_blank'; // открывает в новой вкладке
            anchor.rel = 'noopener noreferrer'; // безопасность

            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);
        } catch (error) {
            ErrorService.handleApiError(error);
        }
    }

    /**
     * @description download Excel file
     * @param id {number} - doc id
     * @param type {enum} - type of a document
     * @param name {string} - name of downloaded doc
     */
    protected async downloadReport(id: number, type: PMPlanChartEnum, name?: string) {
        let title = '';
        let url = `/project-management/${type}/${id}/excel`;

        switch (type) {
            case PMPlanChartEnum.version:
                title = this.$t('page.projectManagement.chart.bpc.title') as string;
                break;
            case PMPlanChartEnum.dpcQuart:
                title = this.$t('page.projectManagement.chart.dpcQuart.title') as string;
                break;
            case PMPlanChartEnum.dpcExec:
                title = this.$t('page.projectManagement.chart.dpcExec.title') as string;
                break;
            case PMPlanChartEnum.weekly:
                title = 'Недельный отчет';
                url = `/project-management/charts/weekly-report/${id}/excel`;
                break;
            default:
                title = 'Chart';
        }

        try {
            let fileName = title;
            if (name) {
                fileName += ` - ${name}`;
            }
            fileName += ` - ${id}.xlsx`;

            await downloadFile({ url, name: fileName });
        } catch (error) {
            ErrorService.handleApiError(error);
        }
    }

    protected async deleteDocument(report: any, reports: any[]) {
        if (report) {
            const id = report.id;
            const title = this.$t(`page.projectManagement.chart.wantDelete`, [report.name]);
            this.$modal.show(ModalEnum.Danger, {
                title,
                acceptLabel: this.$t('delete'),

                onAccept: async () => {
                    try {
                        await deletePMChartDpc(id);
                        this.showDeleteSuccessModal(report.name);

                        const updated = reports.filter(({ id }) => id !== report.id);
                        reports.splice(0, reports.length, ...updated);
                    } catch (error) {
                        ErrorService.handleApiError(error);
                    }
                },
            });
        }
    }

    private showDeleteSuccessModal(name: string) {
        const title = this.$t(`page.projectManagement.chart.didDelete`, [name]);
        const actionLabel = this.$t('good');

        this.$modal.show(ModalEnum.Alert, { title, actionLabel });
    }
}
